/* Feature announcements ("What's New") — logic over the announcement data.
 *
 * The entries themselves live in ./announcements.data (the only file edited to add an
 * announcement, by hand or by the CI drafter). This module just exposes them to the UI and
 * tracks the per-device "seen" marker.
 */
import { announcements, type Announcement, type LocalizedText } from "./announcements.data";

export type { Announcement, AnnouncementKind, LocalizedText } from "./announcements.data";

/** Best available translation for `language` (exact, then base language, then English). */
export function localizedText(text: LocalizedText, language: string): string {
  const base = language.split("-")[0] ?? language;
  return text[language] ?? text[base] ?? text.en;
}

/** The changelog entries, sorted newest first. Only features are surfaced to users — fixes are
    kept out of "What's New" (the CI drafter already omits them; this guards hand-added entries). */
export function useAnnouncements(): Announcement[] {
  return announcements
    .filter((a) => a.kind === "feature")
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

/* ---------------- "new since last visit" state (localStorage; per-device) ---------------- */
const SEEN_KEY = "hg-announcements-seen-v1";

export function latestAnnouncementDate(items: Announcement[]): string {
  return items[0]?.date ?? "";
}

function seenDate(): string {
  try {
    return localStorage.getItem(SEEN_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Record that the user has seen everything up to `latest` (call when the What's New page is viewed). */
export function markAnnouncementsSeen(latest: string): void {
  try {
    if (latest) localStorage.setItem(SEEN_KEY, latest);
  } catch {
    /* localStorage unavailable — badge just won't clear */
  }
}

/** True when there's at least one announcement newer than what the user has already seen. */
export function hasUnseenAnnouncements(items: Announcement[]): boolean {
  const latest = latestAnnouncementDate(items);
  return !!latest && latest > seenDate();
}
